/**
 * `client_action` widget.
 *
 * Two modes, chosen by the payload's `confirm` flag:
 *
 * - `confirm` falsy (default): the action was explicitly commanded by
 *   the user. Auto-fire once after stream `done` (gated by
 *   `autoExecute`/`streaming`), and render a tappable chip so the user
 *   can re-trigger. Single-fire across re-renders (useRef) and remounts
 *   within a session (module-scope `firedKeys`, keyed by
 *   messageId+blockIndex).
 *
 * - `confirm` true: the action is OFFERED off the back of an
 *   informational answer. NEVER auto-fires — renders `label` as a
 *   yes/no prompt and only executes when the user taps 是.
 */

import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { AIClientAction } from '@euler/model/ai';
import {
  ArrowRight,
  Copy as CopyIcon,
  LucideIcon,
  PhoneCall,
  Share2,
} from 'lucide-react-native';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Text, TouchableOpacity, View } from 'react-native';
import { useFireClientAction } from '../../ai/useFireClientAction';

const firedKeys = new Set<string>();

type Props = {
  /** Raw fence payload; narrowed to a typed action by `parseClientAction`. */
  data: unknown;
  messageId?: string;
  blockIndex?: number;
  autoExecute?: boolean;
  streaming?: boolean;
};

export const ClientActionWidget = memo(
  ({ data, messageId, blockIndex, autoExecute, streaming }: Props) => {
    const action = useMemo(() => parseClientAction(data), [data]);
    const fire = useFireClientAction();

    const key = `${messageId ?? '?'}:${blockIndex ?? 0}`;
    const confirm = action?.confirm === true;
    const firedRef = useRef(firedKeys.has(key));

    useEffect(() => {
      // Confirm-gated actions never auto-fire — they wait for a tap.
      if (confirm) return;
      if (!autoExecute || streaming) return;
      if (action == null) return;
      if (firedRef.current) return;
      firedRef.current = true;
      firedKeys.add(key);
      void fire(action);
    }, [autoExecute, streaming, action, fire, key, confirm]);

    if (action == null) {
      return <ActionChipDisabled label="操作信息缺失" />;
    }
    if (confirm) {
      return <ConfirmAction action={action} onConfirm={() => fire(action)} />;
    }
    return <ActionChip action={action} onPress={() => fire(action)} />;
  },
);

// ──────────────────────────────────────────────────────────────────
// confirm prompt — label as a question + 是 / 否
// ──────────────────────────────────────────────────────────────────

const ConfirmAction = memo(
  ({
    action,
    onConfirm,
  }: {
    action: AIClientAction;
    onConfirm: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const [resolved, setResolved] = useState<'yes' | 'no' | null>(null);

    if (resolved === 'no') return null;
    if (resolved === 'yes') {
      return (
        <View style={{ marginTop: rpx(6), marginBottom: rpx(2) }}>
          <Text style={{ color: '#94A3B8', fontSize: rpx(12) }}>
            已为您打开
          </Text>
        </View>
      );
    }
    return (
      <View
        style={{
          marginTop: rpx(8),
          marginBottom: rpx(2),
          padding: rpx(12),
          borderRadius: rpx(12),
          backgroundColor: '#F8FAFC',
          borderWidth: 1,
          borderColor: '#E2E8F0',
        }}
      >
        <Text
          style={{
            color: '#1E293B',
            fontSize: rpx(14),
            marginBottom: rpx(10),
          }}
        >
          {action.label}
        </Text>
        <View style={{ flexDirection: 'row', gap: rpx(10) }}>
          <TouchableOpacity
            activeOpacity={0.7}
            onPress={() => {
              setResolved('yes');
              onConfirm();
            }}
            style={{
              paddingHorizontal: rpx(20),
              paddingVertical: rpx(8),
              borderRadius: rpx(20),
              backgroundColor: '#1D4ED8',
            }}
          >
            <Text
              style={{ color: '#fff', fontSize: rpx(13), fontWeight: '500' }}
            >
              是
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            activeOpacity={0.7}
            onPress={() => setResolved('no')}
            style={{
              paddingHorizontal: rpx(20),
              paddingVertical: rpx(8),
              borderRadius: rpx(20),
              backgroundColor: '#fff',
              borderWidth: 1,
              borderColor: '#CBD5E1',
            }}
          >
            <Text
              style={{ color: '#64748B', fontSize: rpx(13), fontWeight: '500' }}
            >
              否
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  },
);

// ──────────────────────────────────────────────────────────────────
// chip
// ──────────────────────────────────────────────────────────────────

const ActionChip = memo(
  ({ action, onPress }: { action: AIClientAction; onPress: () => void }) => {
    const { rpx } = useResponsiveContext();
    const Icon = iconForAction(action);
    return (
      <TouchableOpacity
        activeOpacity={0.7}
        onPress={onPress}
        style={{
          alignSelf: 'flex-start',
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(6),
          marginTop: rpx(6),
          marginBottom: rpx(2),
          paddingHorizontal: rpx(12),
          paddingVertical: rpx(8),
          borderRadius: rpx(20),
          backgroundColor: '#EFF6FF',
          borderWidth: 1,
          borderColor: '#DBEAFE',
        }}
      >
        <Icon size={rpx(14)} color="#1D4ED8" strokeWidth={2} />
        <Text
          style={{
            color: '#1D4ED8',
            fontSize: rpx(13),
            fontWeight: '500',
          }}
          numberOfLines={1}
        >
          {action.label}
        </Text>
      </TouchableOpacity>
    );
  },
);

const ActionChipDisabled = memo(({ label }: { label: string }) => {
  const { rpx } = useResponsiveContext();
  return (
    <View
      style={{
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        gap: rpx(6),
        marginTop: rpx(6),
        marginBottom: rpx(2),
        paddingHorizontal: rpx(12),
        paddingVertical: rpx(8),
        borderRadius: rpx(20),
        backgroundColor: '#F1F5F9',
        borderWidth: 1,
        borderColor: '#E2E8F0',
      }}
    >
      <Text style={{ color: '#94A3B8', fontSize: rpx(13), fontWeight: '500' }}>
        {label}
      </Text>
    </View>
  );
});

function iconForAction(action: AIClientAction): LucideIcon {
  switch (action.action) {
    case 'navigate':
      return ArrowRight;
    case 'call':
      return PhoneCall;
    case 'copy':
      return CopyIcon;
    case 'share':
      return Share2;
  }
}

// ──────────────────────────────────────────────────────────────────
// payload parser — narrow `any` to a typed action or return null
// ──────────────────────────────────────────────────────────────────

function parseClientAction(data: unknown): AIClientAction | null {
  if (!data || typeof data !== 'object') return null;
  const obj = data as Record<string, unknown>;
  if (typeof obj.label !== 'string' || obj.label.length === 0) return null;
  switch (obj.action) {
    case 'navigate':
      return parseNavigate(obj);
    case 'call':
      return parseCall(obj);
    case 'copy':
      return parseCopy(obj);
    case 'share':
      return parseShare(obj);
    default:
      return null;
  }
}

const confirmOf = (obj: Record<string, unknown>) =>
  obj.confirm === true ? { confirm: true } : {};

function parseNavigate(obj: Record<string, unknown>): AIClientAction | null {
  if (typeof obj.page !== 'string') return null;
  // Params are forwarded as-is to navigateForSlug; type assertion is
  // safe because the resolver switch covers every AIPageSlug.
  return {
    action: 'navigate',
    page: obj.page,
    params: (obj.params ?? {}) as any,
    label: obj.label as string,
    ...confirmOf(obj),
  } as AIClientAction;
}

function parseCall(obj: Record<string, unknown>): AIClientAction | null {
  if (typeof obj.phone !== 'string' || obj.phone.length === 0) return null;
  return {
    action: 'call',
    phone: obj.phone,
    label: obj.label as string,
    ...confirmOf(obj),
  };
}

function parseCopy(obj: Record<string, unknown>): AIClientAction | null {
  if (typeof obj.text !== 'string' || obj.text.length === 0) return null;
  return {
    action: 'copy',
    text: obj.text,
    label: obj.label as string,
    ...(typeof obj.toast === 'string' ? { toast: obj.toast } : {}),
    ...confirmOf(obj),
  };
}

function parseShare(obj: Record<string, unknown>): AIClientAction | null {
  if (obj.shareType !== 'report') return null;
  if (
    typeof obj.orderNo !== 'string' ||
    typeof obj.reportNo !== 'string' ||
    typeof obj.licensePlateNo !== 'string' ||
    typeof obj.reportType !== 'string'
  ) {
    return null;
  }
  const shareMethod =
    obj.shareMethod === 'wechat' ||
    obj.shareMethod === 'wework' ||
    obj.shareMethod === 'sms'
      ? obj.shareMethod
      : undefined;
  const shareTarget =
    obj.shareTarget === 'customer' || obj.shareTarget === 'sa'
      ? obj.shareTarget
      : undefined;
  return {
    action: 'share',
    shareType: 'report',
    orderNo: obj.orderNo,
    reportNo: obj.reportNo,
    licensePlateNo: obj.licensePlateNo,
    reportType: obj.reportType as any,
    ...(shareMethod ? { shareMethod } : {}),
    ...(shareTarget ? { shareTarget } : {}),
    label: obj.label as string,
    ...confirmOf(obj),
  };
}
